import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import FolderOutlinedIcon from "@material-ui/icons/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SyncIcon from "@material-ui/icons/Sync";
import RefreshIcon from "@material-ui/icons/Refresh";
import SvgIcon from "@material-ui/core/SvgIcon";
import Swal from "sweetalert2";
import { CustomTable, ModalError, SearchTextInput } from "components";
import { getErrors } from "utils";
import { listLocalFiles, retryDownload } from "utils/DSPSFTPService";

const TABLE_MAX_HEIGHT = 460;

const useStyles = makeStyles(() => ({
  folderIcon: { marginRight: 8, color: "#9e9e9e" },
  fileIcon: { marginRight: 8, color: "#9e9e9e" },
  statusChipSuccess: {
    fontSize: 11,
    backgroundColor: "#4caf50",
    color: "white",
  },
  statusChipFailed: {
    fontSize: 11,
    backgroundColor: "#f44336",
    color: "white",
  },
  pathText: { fontFamily: "monospace", fontSize: 13 },
  folderClickable: {
    cursor: "pointer",
    "&:hover": { color: "#1976d2" },
  },
  chipMarginLeft: { marginLeft: 8 },
  progressLabel: { fontSize: 7, fontWeight: "bold" },
  queueChip: { height: 20, fontSize: 10, marginLeft: 8 },
  retryButton: { marginLeft: 4, padding: 2 },
  syncIcon: { fontSize: 18, color: "#424242" },
  successText: { color: "#4caf50", fontSize: 13 },
  successIcon: { color: "#4caf50", fontSize: 18 },
  failedText: { color: "#f44336", fontSize: 13 },
  failedIcon: { color: "#f44336", fontSize: 18 },
  sizeText: {
    color: "#9e9e9e",
    fontSize: 10,
    marginLeft: 8,
    whiteSpace: "nowrap",
  },
  speedText: {
    color: "#1976d2",
    fontSize: 10,
    marginTop: 4,
    whiteSpace: "nowrap",
  },
}));

const FolderUpIcon = props => (
  <SvgIcon {...props}>
    <path d="M20 6H12L10 4H4C2.89 4 2 4.89 2 6V18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V8C22 6.89 21.11 6 20 6ZM20 18H4V8H20V18ZM13 13V17H11V13H8L12 9L16 13H13Z" />
  </SvgIcon>
);

const CircularProgressWithLabel = ({ value, ...props }) => {
  const classes = useStyles();
  const numericValue =
    typeof value === "number" ? value : parseFloat(value || 0);
  const displayLabel =
    numericValue < 10 && numericValue > 0 && !Number.isInteger(numericValue)
      ? `${numericValue.toFixed(1)}%`
      : `${Math.round(numericValue)}%`;

  return (
    <Box
      position="relative"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      ml={1}
    >
      <CircularProgress
        variant="determinate"
        size={24}
        value={numericValue}
        {...props}
      />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
          className={classes.progressLabel}
        >
          {displayLabel}
        </Typography>
      </Box>
    </Box>
  );
};

/** Format file size to human-readable string */
const formatFileSize = sizeStr => {
  if (!sizeStr && sizeStr !== 0) return "";
  if (typeof sizeStr === "string" && /[a-zA-Z]/.test(sizeStr)) return sizeStr;
  const bytes = typeof sizeStr === "string" ? parseInt(sizeStr, 10) : sizeStr;
  if (isNaN(bytes) || bytes < 0) return String(sizeStr);
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${
    value < 10 && i > 0
      ? value.toFixed(2)
      : value < 100 && i > 0
      ? value.toFixed(1)
      : Math.round(value)
  } ${units[i]}`;
};

function GlusterExplorer({ dsp, refreshTrigger }) {
  const classes = useStyles();

  const [currentPath, setCurrentPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const dspSlug = dsp?.slug || dsp?.label?.toLowerCase()?.replace(/\s+/g, "-");

  // ─── Fetch local (Gluster) listing ──────────────────────────────────────────
  const fetchFiles = useCallback(
    async (path = "", search = "", isBackground = false) => {
      if (!dspSlug) return;
      if (!isBackground) setIsLoading(true);
      try {
        const res = await listLocalFiles(dspSlug, path, search);
        const payload = res?.data?.data || res?.data || {};
        const filesArray = Array.isArray(payload)
          ? payload
          : payload.files || [];
        setFiles(filesArray);
      } catch (error) {
        if (!isBackground) ModalError(getErrors(error?.response));
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    },
    [dspSlug]
  );

  useEffect(() => {
    if (dspSlug) {
      setCurrentPath("");
      setPathHistory([]);
      setSearchQuery("");
      fetchFiles("", "");
    }
  }, [dspSlug, fetchFiles]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const handler = setTimeout(() => {
      if (dspSlug) {
        fetchFiles(currentPath, searchQuery);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Refresh when download completes in SFTPExplorer
  useEffect(() => {
    if (refreshTrigger && dspSlug) {
      fetchFiles(currentPath, "", true);
    }
  }, [refreshTrigger, dspSlug, currentPath, fetchFiles]);

  // Auto-refresh progress silently every 3 seconds if any item has active progress
  useEffect(() => {
    const hasActiveProgress = files.some(
      f =>
        f.progress_flag === true ||
        f.progress_flag === 1 ||
        f.progress_flag === "true" ||
        f.download_status === "R"
    );
    if (!hasActiveProgress || !dspSlug) return;

    const interval = setInterval(() => {
      fetchFiles(currentPath, "", true);
    }, 5000);

    return () => clearInterval(interval);
  }, [files, dspSlug, currentPath, fetchFiles]);

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const handleNavigateInto = item => {
    if (!item.is_dir && item.type !== "folder" && item.type !== "directory")
      return;
    const newPath =
      item.path ||
      (currentPath === "" ? item.name : `${currentPath}/${item.name}`);
    setPathHistory(prev => [...prev, currentPath]);
    setCurrentPath(newPath);
    setSearchQuery("");
    fetchFiles(newPath, "");
  };

  const handleNavigateBack = () => {
    const prev = pathHistory[pathHistory.length - 1] ?? "";
    setPathHistory(h => h.slice(0, -1));
    setCurrentPath(prev);
    setSearchQuery("");
    fetchFiles(prev, "");
  };

  // ─── Sync ───────────────────────────────────────────────────────────────────
  const handleRefresh = () => {
    fetchFiles(currentPath, searchQuery);
  };

  const handleRetryItem = async item => {
    try {
      await retryDownload({
        process_id: item.process_id || item.id,
        dsp_slug: dspSlug,
        path: item.path || item.name,
      });
      Swal.fire({
        icon: "info",
        title: "Retry Started",
        text: `Retrying download for ${item.name}…`,
        timer: 3000,
        showConfirmButton: false,
      });
      fetchFiles(currentPath, "", true);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  // ─── Columns ─────────────────────────────────────────────────────────────────
  const columnTable = [
    {
      name: "all",
      title: (
        <span className={classes.pathText}>
          /data/mpis/dsp/{dspSlug}/{currentPath === "" ? "" : currentPath}
        </span>
      ),
      renderText: item => {
        if (!item || typeof item !== "object") return null;
        if (item.is_back_button) {
          return (
            <Box
              display="flex"
              alignItems="center"
              className={classes.folderClickable}
              onClick={() => handleNavigateBack()}
            >
              <FolderUpIcon className={classes.folderIcon} />
              ...
            </Box>
          );
        }
        const isFolder =
          item.is_dir || item.type === "folder" || item.type === "directory";
        const status = item.progress_flag
          ? item.download_status
          : item.download_status ||
            (item.status === "success"
              ? "F"
              : item.status === "failed"
              ? "E"
              : null);

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box
              display="flex"
              alignItems="center"
              className={isFolder ? classes.folderClickable : ""}
              onClick={() => isFolder && handleNavigateInto(item)}
            >
              {isFolder ? (
                <FolderOutlinedIcon className={classes.folderIcon} />
              ) : (
                <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
              )}
              <Box display="flex" flexDirection="column">
                <Box display="flex" alignItems="center">
                  {item.name}
                  {/* File size */}
                  {item.size != null && (
                    <Typography component="span" className={classes.sizeText}>
                      {" "}
                      ({formatFileSize(item.size)})
                    </Typography>
                  )}
                </Box>
                {/* Download Speed */}
                {status === "R" && item.download_speed && (
                  <Typography
                    variant="caption"
                    component="div"
                    className={classes.speedText}
                  >
                    {item.download_speed}
                  </Typography>
                )}
              </Box>

              {status === "R" && (
                <Box display="flex" alignItems="center" ml={2}>
                  <CircularProgressWithLabel value={item.download_progress} />
                </Box>
              )}
              {status === "Q" && (
                <Chip
                  label="Queue"
                  size="small"
                  variant="outlined"
                  color="info"
                  className={classes.queueChip}
                />
              )}

              {status === "E" && (
                <Tooltip title="Retry Download">
                  <IconButton
                    size="small"
                    className={classes.retryButton}
                    onClick={e => {
                      e.stopPropagation();
                      handleRetryItem(item);
                    }}
                  >
                    <SyncIcon fontSize="small" className={classes.syncIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {item?.progress_flag && (
              <Box display="flex" alignItems="center" gap={0.5}>
                {status === "F" && (
                  <>
                    <Typography
                      variant="body2"
                      fontStyle="italic"
                      className={classes.successText}
                    >
                      Download Success
                    </Typography>
                    <CheckIcon className={classes.successIcon} />
                  </>
                )}

                {status === "E" && (
                  <>
                    <Typography
                      variant="body2"
                      fontStyle="italic"
                      className={classes.failedText}
                    >
                      Download Failed
                    </Typography>
                    <CloseIcon className={classes.failedIcon} />
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      },
    },
  ];

  const allData =
    pathHistory.length > 0
      ? [{ is_back_button: true, name: "...", is_dir: true }, ...files]
      : files;

  const filteredData = searchQuery
    ? allData.filter(
        item =>
          item.is_back_button ||
          item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allData;

  return (
    <Box>
      <Grid container justifyContent="space-between" alignItems="center" mb={1}>
        <Grid item display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight="bold">
            GLUSTER MPIS
          </Typography>
        </Grid>
        <Grid item>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Box mb={1}>
        <SearchTextInput
          placeholder="Search files..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          clear={true}
          onClear={() => setSearchQuery("")}
          size="small"
          width="100%"
        />
      </Box>
      <CustomTable
        isLoading={isLoading}
        columnTable={columnTable}
        data={filteredData}
        maxHeight={TABLE_MAX_HEIGHT}
        isPagination={false}
      />
    </Box>
  );
}

export default GlusterExplorer;
